<script setup lang="ts">
import type { SectionContactRecord } from '~/types/datocms'

const store = useAppStore()

const props = defineProps<{
  data: SectionContactRecord
  moduleIndex: number
}>()

const { level, isSemantic } = useSectionRules(props.moduleIndex, !!props.data.title)

const handleExternalLink = (button: CtaRecord) => {
  if (!button.recordLink && !button.externalLink) {
    console.warn(`No link provided for "${button.title}" call to action`, button)
    return undefined
  } else if (button.externalLink?.startsWith('#')) {
    return {
      path: useLocalePathByType(
        button.recordLink?._modelApiKey ?? '',
        button.recordLink?.slug as string
      ),
      hash: button.externalLink
    }
  } else if (button.externalLink?.startsWith('?')) {
    return (
      useLocalePathByType(
        button.recordLink?._modelApiKey ?? '',
        button.recordLink?.slug as string
      ) + button.externalLink
    )
  } else if (button.externalLink?.startsWith('http')) {
    return button.externalLink
  } else if (button.recordLink) {
    return useLocalePathByType(button.recordLink?._modelApiKey, button.recordLink?.slug as string)
  }
}

onMounted(() => {
  store.setGradientHeader('grey')
  store.setShowFooter(false)
})
onBeforeUnmount(() => {
  store.setShowFooter(true)
})
</script>

<template>
  <div class="bg-gradient-primary min-h-dvh bg-grey-dark pt-20 lg:pt-30">
    <div class="grid-default gap-y-20 px-4 pb-20 lg:gap-y-68 lg:px-8 lg:pb-30 lg:pt-10">
      <Heading
        v-if="data.title"
        :level="level"
        :size="6"
        :is-semantic="isSemantic"
        class="col-span-full max-w-[680px] uppercase lg:col-span-6"
        >{{ data.title }}</Heading
      >
      <div
        class="col-span-full flex flex-col gap-4 text-4xl font-light uppercase lg:order-3 lg:gap-6"
      >
        <a class="group inline-flex gap-2" href="mailto:production@paktmedia.com">
          <span>production</span>
          <span
            class="hidden text-black transition-colors duration-300 group-hover:text-yellow lg:inline"
            >@paktmedia.com</span
          >
          <Icon name="custom:arrow-right" class="shrink-0 -rotate-45 lg:hidden" size="16" />
        </a>
        <a class="group inline-flex gap-2" href="mailto:office@paktmedia.com">
          <span>office</span>
          <span
            class="hidden text-black transition-colors duration-300 group-hover:text-yellow lg:inline"
            >@paktmedia.com</span
          >
          <Icon name="custom:arrow-right" class="shrink-0 -rotate-45 lg:hidden" size="16" />
        </a>
        <a class="group inline-flex gap-2" href="mailto:jobs@paktmedia.com">
          <span>jobs</span>
          <span
            class="hidden text-black transition-colors duration-300 group-hover:text-yellow lg:inline"
            >@paktmedia.com</span
          >
          <Icon name="custom:arrow-right" class="shrink-0 -rotate-45 lg:hidden" size="16" />
        </a>
      </div>
      <div class="col-span-full flex gap-4 uppercase lg:hidden">
        <div class="flex flex-1 flex-col gap-4">
          <span>Croatia</span>
          <address class="flex flex-col not-italic">
            <span>Savska cesta 118</span><span>10000 Zagreb</span><span>Croatia</span>
          </address>
          <span class="text-black"> VAT N°: HR 61591443694 </span>
        </div>
        <div class="flex flex-1 flex-col gap-4">
          <span>Slovenia</span>
          <address class="flex flex-col not-italic">
            <span>Prešernov trg 1</span><span>1000 Ljubljana</span><span>Slovenia</span>
          </address>
          <span class="text-black"> VAT N°: SI 11875356 </span>
        </div>
      </div>
      <div
        class="col-span-full lg:order-2 lg:col-span-4 lg:col-start-8 lg:block xl:col-start-9 hd:col-start-10"
      >
        <SocialLinks :data="store.allSocialLinks" />
        <div v-if="data.links?.length" class="mt-10 flex flex-col text-yellow lg:mt-14">
          <HyperLink
            v-for="(link, idx) in data.links"
            :key="idx"
            class="w-fit py-1 transition-colors duration-300 hover:text-grey-light"
            :to="handleExternalLink(link)"
            :target="link.externalLink?.startsWith('http') ? '_blank' : undefined"
            :content="link.title ?? ''"
            rotate-icon
          />
        </div>
      </div>
    </div>
    <div class="grid-default gap-y-0 px-4 pb-4 lg:px-8 lg:pb-8">
      <div class="col-span-6 hidden gap-4 uppercase lg:flex">
        <div class="flex flex-1 flex-col gap-6">
          <span>Croatia</span>
          <address class="flex flex-col not-italic">
            <span>Savska cesta 118</span><span>10000 Zagreb</span><span>Croatia</span>
          </address>
          <span class="text-black"> VAT N°: HR 61591443694 </span>
        </div>
        <div class="flex flex-1 flex-col gap-6">
          <span>Slovenia</span>
          <address class="flex flex-col not-italic">
            <span>Prešernov trg 1</span><span>1000 Ljubljana</span><span>Slovenia</span>
          </address>
          <span class="text-black"> VAT N°: SI 11875356 </span>
        </div>
      </div>
      <div
        v-if="store.allUsefulLinks?.length"
        class="col-span-full flex flex-col gap-4 text-cream lg:col-span-4 lg:col-start-8 lg:gap-6 xl:col-start-9 hd:col-start-10"
      >
        <span class="uppercase lg:text-lg">Useful links</span>
        <div class="flex flex-col">
          <HyperLink
            v-for="(link, idx) in store.allUsefulLinks"
            :key="idx"
            class="w-fit py-1 transition-colors duration-300 hover:text-grey-light"
            :to="link.url"
            target="_blank"
            :content="link.title ?? ''"
            rotate-icon
          />
        </div>
      </div>
      <div class="col-span-full mt-20 text-black lg:mt-30">
        <FooterLinks :data="store.allFooterLinks" class="lg:!ml-0" />
      </div>
    </div>
  </div>
</template>

<style lang="postcss" module>
.contactBody {
  @apply text-nowrap text-3xl font-light uppercase md:text-4xl;

  a {
    @apply mt-4 inline-block lg:mt-6;
  }

  b {
    @apply font-light text-black;
  }
}
</style>
